import { Injectable } from '@angular/core';
import { CircularBuffer } from 'ops/common/circular-buffer';
import { ServerSideEventService } from './serverSideEvent.service';
import { Subscription } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ChannelHistoryValuesService {
    private histories: any = {};
    private historyLength = 2000;
    private updateInterval = 1000;
    private epoch = 0;

    serverEventSubscription: Subscription;

    constructor(private serverSideEvent: ServerSideEventService) {
        this.roundEpochToNearestInterval();
    }

    startUpdates() {
        this.serverEventSubscription = this.serverSideEvent.onChannelDataReceived.subscribe(
            (channelsData) => {
                for (let i = 0; i < channelsData.length; i++) {
                    if (!this.channelHistoryExists(channelsData[i].id)) {
                        this.addChannel(channelsData[i].id);
                    }

                    this.appendValueToHistory(channelsData[i].id, channelsData[i].value);
                }
            },
        );
    }

    stopUpdates() {
        this.serverEventSubscription?.unsubscribe();
    }

    private channelHistoryExists(channelId: any) {
        return channelId in this.histories;
    }

    private addChannel(channelId: any) {
        this.histories[channelId] = {
            log: new CircularBuffer(this.historyLength),
            lastUpdated: null,
        };
    }

    private appendValueToHistory(channelNumber: any, value: any) {
        const history = this.histories[channelNumber];
        const updateTimestamp = this.getCurrentUpdateWindowEnd(Date.now());

        const missingDataCount =
            history.lastUpdated !== null
                ? Math.min(
                      Math.floor((updateTimestamp - history.lastUpdated) / this.updateInterval) - 1,
                      this.historyLength,
                  )
                : 0;

        for (let i = 0; i < missingDataCount; i++) history.log.appendValue(null);

        if (history.lastUpdated == updateTimestamp) {
            history.log.setValue(history.log.length - 1, value);
        } else {
            history.log.appendValue(value);
            history.lastUpdated = updateTimestamp;
        }
    }

    private roundEpochToNearestInterval() {
        this.epoch = 0;
        this.epoch = this.getLastCompletedUpdateTimestamp(Date.now());
    }

    private getCurrentUpdateWindowEnd(time: any) {
        return (
            this.epoch + Math.ceil((time - this.epoch) / this.updateInterval) * this.updateInterval
        );
    }

    private getLastCompletedUpdateTimestamp(time: any) {
        return (
            this.epoch + Math.floor((time - this.epoch) / this.updateInterval) * this.updateInterval
        );
    }

    public getHistory(channels: any, lookback?: any) {
        if (!Array.isArray(channels)) {
            channels = [channels];
        }
        for (let i = 0; i < channels.length; i++) channels[i] = Number(channels[i]);

        const lastTimestamp = this.getLastCompletedUpdateTimestamp(Date.now());

        const returnedHistories = Array(channels.length);
        const currentTime = Date.now();
        const currentUpdateWindowEnd = this.getCurrentUpdateWindowEnd(currentTime);

        for (let i = 0; i < channels.length; i++) {
            const channelHistory = this.histories[channels[i]];

            if (!channelHistory) {
                returnedHistories[i] = {
                    fcuChannelId: channels[i],
                    log: [],
                    lastUpdated: 0,
                };
            } else {
                let end = channelHistory.log.length;
                let lastUpdated = channelHistory.lastUpdated;

                //Don't append the value of the update window in progress
                if (lastUpdated == currentUpdateWindowEnd) {
                    end--;
                    lastUpdated = this.getLastCompletedUpdateTimestamp(currentTime);
                }

                const start = Math.max(end - (lookback || this.historyLength), 0);
                const valueHistory = Array(end - start);

                for (let i = start; i < end; i++) {
                    valueHistory[i - start] = channelHistory.log.getValue(i);
                }

                returnedHistories[i] = {
                    fcuChannelId: channels[i],
                    log: valueHistory,
                    lastUpdated: lastUpdated,
                };
            }
        }

        return {
            histories: returnedHistories,
            updateInterval: this.updateInterval,
            currentTime: lastTimestamp,
        };
    }
}
